import { useState, useEffect, useCallback } from "react";
import { useAtomValue, useAtom } from "jotai";
import { useClient } from "urql";

import { fetchStoreLocatorList } from "@lululemon/ecom-common/src/store/actions/store-locations";
import storeLocatorListAtom from "@lululemon/ecom-common/src/components/bopis/jotai/atoms/storeLocatorListAtom";
import { geocodeAddress } from "@lululemon/ecom-common/src/services/googlemaps";
import isBopisModalOpenAtom from "@lululemon/ecom-common/src/components/bopis/jotai/atoms/isBopisModalOpenAtom";
import fetchStoreInventory from "@lululemon/ecom-common/src/graphql/fetchStoreInventory";
import { getStoresByProximity } from "../utils/getStoresByProximity";
import {
  filterNearbyStoresForBopisInventory,
  filterNearbyStoresForStoreInventory,
} from "../utils/filterNearbyStoresInventory";
import Util from "../../../lib/util";
import log from "../logger";
import isAccordionOpenAtom from "../jotai/atoms/isAccordionOpenAtom";
import useSelectedStore from "./use-selected-store";
import useUserLocationAtom from "./use-user-location-atom";
import useBopisInventory from "./use-bopis-inventory";
import useDistanceFilter from "./use-distance-filter";

export default function useNearbyBopisStores({
  activeSku,
  shouldFetchInventory,
}) {
  const [nearbyBopisStores, setNearbyBopisStores] = useState(null);
  const [
    nearbyStoresWithInventoryButNoBopis,
    setNearbyStoresWithInventoryButNoBopis,
  ] = useState(null);
  const [userSearchedLocation, setUserSearchedLocation] = useState(null);
  const [searchQueryError, setSearchQueryError] = useState(null);
  const [userSearchedQuery, setUserSearchedQuery] = useState(null);
  const [isLoadingUserSearchQuery, setIsLoadingUserSearchQuery] =
    useState(false);

  const [allLululemonStores, setAllLululemonStores] =
    useAtom(storeLocatorListAtom);
  const { distanceFilter } = useDistanceFilter();
  const isBopisModalOpen = useAtomValue(isBopisModalOpenAtom);
  const isAccordionOpen = useAtomValue(isAccordionOpenAtom);
  const { bopisInventory, setBopisInventory } = useBopisInventory();
  const { userLocation } = useUserLocationAtom();
  const { selectedStore, setSelectedStore } = useSelectedStore();

  const countryCode = Util.getShippingLocation();
  const graphqlClient = useClient();

  useEffect(() => {
    const getNearbyStoresWithInventory = async () => {
      let storeLocatorList;
      if (!allLululemonStores) {
        storeLocatorList = await fetchStoreLocatorList(graphqlClient);
        setAllLululemonStores(storeLocatorList);
      }
      const allStores = allLululemonStores || storeLocatorList;
      const location = userSearchedLocation || userLocation;
      const nearbyStores = getStoresByProximity(
        allStores,
        location,
        countryCode,
        distanceFilter
      );
      const nearbyStoresIds = nearbyStores.map((store) => store.id);
      const nearbyStoresInventory = await fetchStoreInventory(
        graphqlClient,
        activeSku.id,
        nearbyStoresIds
      );

      const nearbyStoresWithBopisQuantity = filterNearbyStoresForBopisInventory(
        distanceFilter,
        nearbyStoresInventory,
        nearbyStores
      );

      if (
        nearbyStoresWithBopisQuantity?.length > 0 &&
        selectedStore.id &&
        bopisInventory.bopisQuantity == null
      ) {
        const store = nearbyStoresWithBopisQuantity.find(
          (store) => store.id === selectedStore.id
        );
        setSelectedStore({
          ...selectedStore,
          distanceToStore: store.distanceToStore,
        });
        setBopisInventory({
          ...bopisInventory,
          bopisQuantity: store.bopisQuantity,
        });
      }

      const nearbyStoresWithQuantity = filterNearbyStoresForStoreInventory(
        distanceFilter,
        nearbyStoresInventory,
        nearbyStores
      );

      if (nearbyStoresWithBopisQuantity && isLoadingUserSearchQuery) {
        setIsLoadingUserSearchQuery(false);
      }

      setNearbyBopisStores(nearbyStoresWithBopisQuantity);
      setNearbyStoresWithInventoryButNoBopis(nearbyStoresWithQuantity);
    };

    const getNearbyStores = async () => {
      // just get stores by proximity
      let storeLocatorList;
      if (!allLululemonStores) {
        storeLocatorList = await fetchStoreLocatorList(graphqlClient);
        setAllLululemonStores(storeLocatorList);
      }
      const allStores = allLululemonStores || storeLocatorList;
      const location = userSearchedLocation || userLocation;
      const nearbyStores = getStoresByProximity(
        allStores,
        location,
        countryCode,
        distanceFilter
      );

      if (nearbyStores) {
        setIsLoadingUserSearchQuery(false);
      }

      setNearbyBopisStores(nearbyStores);
    };

    try {
      const hasUserLocation =
        userLocation.city ||
        userLocation.address ||
        userSearchedLocation?.address;
      if (!shouldFetchInventory && hasUserLocation && isBopisModalOpen) {
        getNearbyStores();
      } else if (
        hasUserLocation &&
        activeSku?.id &&
        (isBopisModalOpen || isAccordionOpen)
      ) {
        getNearbyStoresWithInventory();
      }
    } catch (error) {
      setIsLoadingUserSearchQuery(false);
      log.error(error, {
        tags: {
          functionName: useNearbyBopisStores.name,
        },
      });
    }
  }, [
    userLocation?.address,
    userLocation?.city,
    activeSku?.id,
    isBopisModalOpen,
    isAccordionOpen,
    distanceFilter,
    userSearchedLocation,
    shouldFetchInventory,
  ]);

  const getAddressFromSearchQuery = useCallback(
    async (searchQuery) => {
      try {
        setIsLoadingUserSearchQuery(true);
        const address = await geocodeAddress(searchQuery, countryCode);
        setUserSearchedQuery(searchQuery);
        if (searchQueryError && address) {
          setSearchQueryError(null);
        }

        if (!address.address) {
          setIsLoadingUserSearchQuery(false);
          return;
        }
        return address;
      } catch (e) {
        setIsLoadingUserSearchQuery(false);
        setSearchQueryError(e);
      }
    },
    [allLululemonStores, distanceFilter, searchQueryError]
  );

  return {
    isLoadingUserSearchQuery,
    userSearchedLocation,
    userSearchedQuery,
    setUserSearchedLocation,
    nearbyBopisStores,
    nearbyStoresWithInventoryButNoBopis,
    getAddressFromSearchQuery,
    searchQueryError,
    setSearchQueryError,
  };
}
